import { useRef, useEffect, useState, useCallback } from 'react';
import { gsap } from 'gsap';
import { Link } from 'react-router-dom';
import './AccordionGallery.css';

// AccordionGallery adapted from React Bits.
// Each item accepts an `image`, `label`, `link`, plus Radius-specific extras:
// `industry` (small red chip on hover) and `stats` (array of {value, label}
// overlaid on the expanded panel). Panels render as internal <Link>s so they
// open in the current window.
const AccordionGallery = ({
  items = [],
  defaultIndex = 0,
  accentColor = '#EF4444',
  overlayColor = '#0a1224',
  textColor = '#ffffff',
  height = 520,
  gap = 10,
  radius = 16,
  expandRatio = 0.52,
  orientation = 'horizontal',
  duration = 0.6,
  ease = 'power3.out',
  parallax = 0.5,
  tilt = 8,
  stagger = 0.06,
  trigger = 'hover',
  showLabels = true,
  grayscale = true,
  className = '',
}) => {
  const rootRef = useRef(null);
  const panelRefs = useRef([]);
  const mediaRefs = useRef([]);
  const barRefs = useRef([]);
  const textRefs = useRef([]);
  const tlRef = useRef(null);
  const firstRunRef = useRef(true);
  const mediaSizeRef = useRef(320);
  const vertical = orientation === 'vertical';
  const count = items.length;
  const [active, setActive] = useState(
    Math.min(Math.max(defaultIndex, 0), count - 1)
  );
  const prefersReduced =
    typeof window !== 'undefined' && window.matchMedia
      ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
      : false;

  const applyLayout = useCallback(
    (animate) => {
      const panels = panelRefs.current;
      if (!panels.length) return;
      const r = Math.min(Math.max(expandRatio, 0.2), 0.9);
      const grow = count > 1 ? (r * (count - 1)) / (1 - r) : 1;
      const mediaSize = mediaSizeRef.current;
      tlRef.current?.kill();
      const dur = animate && !prefersReduced ? duration : 0;
      const tl = gsap.timeline();
      panels.forEach((panel, i) => {
        if (!panel) return;
        const isActive = i === active;
        const media = mediaRefs.current[i];
        const bar = barRefs.current[i];
        const text = textRefs.current[i];
        const rot = isActive ? 0 : i < active ? tilt : -tilt;
        const rotProp = vertical ? { rotateX: -rot } : { rotateY: rot };
        tl.to(panel, { flexGrow: isActive ? grow : 1, ...rotProp, duration: dur, ease }, 0);
        if (media) {
          const drift = Math.max(-1.5, Math.min(1.5, active - i));
          const shift = drift * parallax * mediaSize * 0.06;
          const gray = grayscale ? (isActive ? 0 : 1) : 0;
          tl.to(
            media,
            {
              xPercent: -50,
              yPercent: -50,
              x: vertical ? 0 : isActive ? 0 : shift,
              y: vertical ? (isActive ? 0 : shift) : 0,
              '--ag-gray': gray,
              '--ag-dim': isActive ? 0 : 0.35,
              duration: dur,
              ease,
            },
            0
          );
        }
        if (showLabels && bar && text) {
          if (isActive) {
            tl.to(
              [bar, text],
              { opacity: 1, x: 0, duration: dur, ease, stagger: prefersReduced ? 0 : stagger },
              0
            );
          } else {
            tl.to([bar, text], { opacity: 0, x: -14, duration: dur * 0.6, ease }, 0);
          }
        }
      });
      tlRef.current = tl;
    },
    [
      active,
      count,
      expandRatio,
      duration,
      ease,
      vertical,
      tilt,
      parallax,
      grayscale,
      showLabels,
      stagger,
      prefersReduced,
    ]
  );

  useEffect(() => {
    const el = rootRef.current;
    if (!el) return;
    const measure = () => {
      const rect = el.getBoundingClientRect();
      const total = vertical ? rect.height : rect.width;
      const usable = Math.max(total - gap * (count - 1), 120);
      const size = Math.max(
        140,
        usable * Math.min(Math.max(expandRatio, 0.2), 0.9) * 1.22
      );
      mediaSizeRef.current = size;
      el.style.setProperty('--ag-media-size', `${size}px`);
      applyLayout(!firstRunRef.current);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [applyLayout, gap, count, expandRatio, vertical]);

  useEffect(() => {
    applyLayout(!firstRunRef.current);
    firstRunRef.current = false;
  }, [applyLayout]);

  useEffect(
    () => () => {
      tlRef.current?.kill();
    },
    []
  );

  const handleEnter = (i) => {
    if (trigger === 'hover') setActive(i);
  };
  const handleClickPanel = (i, e) => {
    // First click just expands. A follow-up click on an already-active panel
    // follows the <Link>. This keeps hover-driven browsing intact on desktop
    // while still allowing touch users to open a case study.
    if (i !== active) {
      e.preventDefault();
      setActive(i);
    }
  };
  const handleKeyDown = (i, e) => {
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((i + 1) % count);
    } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((i - 1 + count) % count);
    }
  };

  return (
    <div
      ref={rootRef}
      className={`accordion-gallery${vertical ? ' accordion-gallery--vertical' : ''}${
        className ? ` ${className}` : ''
      }`}
      style={{
        '--ag-accent': accentColor,
        '--ag-overlay': overlayColor,
        '--ag-text': textColor,
        '--ag-gap': `${gap}px`,
        '--ag-radius': `${radius}px`,
        height: vertical ? `${Math.round(height * 1.6)}px` : `${height}px`,
      }}
      role="list"
      aria-label="Case studies accordion gallery"
    >
      {items.map((item, i) => {
        const isActive = i === active;
        const commonProps = {
          ref: (el) => (panelRefs.current[i] = el),
          className: `ag-panel${isActive ? ' ag-panel--active' : ''}`,
          style: { borderRadius: `${radius}px` },
          onClick: (e) => handleClickPanel(i, e),
          onMouseEnter: () => handleEnter(i),
          onFocus: () => setActive(i),
          onKeyDown: (e) => handleKeyDown(i, e),
          role: 'listitem',
          tabIndex: 0,
          'aria-current': isActive ? 'true' : undefined,
          'aria-label': item.label,
          'data-testid': item.testId,
        };
        // Panel body — identical for both wrapper tags.
        const body = (
          <>
            <span className="ag-panel__frame">
              <span className="ag-panel__media" ref={(el) => (mediaRefs.current[i] = el)}>
                <img
                  src={item.image}
                  alt={item.alt || item.label || ''}
                  draggable="false"
                />
              </span>
              <span className="ag-panel__overlay" aria-hidden="true" />
            </span>

            {/* Stats overlay — top-right on expanded panel */}
            {Array.isArray(item.stats) && item.stats.length > 0 && (
              <span className="ag-panel__stats" aria-hidden={!isActive}>
                {item.stats.slice(0, 4).map((s, idx) => (
                  <span key={idx} className="ag-stat">
                    <span className="ag-stat__value">{s.value}</span>
                    <span className="ag-stat__label">{s.label}</span>
                  </span>
                ))}
              </span>
            )}

            {showLabels && (
              <span className="ag-panel__label" aria-hidden="true">
                <span
                  className="ag-panel__bar"
                  ref={(el) => (barRefs.current[i] = el)}
                />
                <span
                  className="ag-panel__text"
                  ref={(el) => (textRefs.current[i] = el)}
                >
                  {item.industry && (
                    <span className="ag-panel__industry">{item.industry}</span>
                  )}
                  <h3 className="ag-panel__title">{item.label}</h3>
                  {item.subtitle && (
                    <p className="ag-panel__subtitle">{item.subtitle}</p>
                  )}
                </span>
              </span>
            )}
          </>
        );

        // React Router Link (same window) when a `to` prop is provided,
        // otherwise fall back to a plain <a href> which also stays in-window.
        return item.to ? (
          <Link to={item.to} {...commonProps} key={i}>
            {body}
          </Link>
        ) : (
          <a href={item.link || '#'} {...commonProps} key={i}>
            {body}
          </a>
        );
      })}
    </div>
  );
};

export default AccordionGallery;
